import { isMaybeMermaidDefinition } from "../../../mermaid";

/**
 * Mermaid diagrams are usually shared as markdown code blocks
 * (```mermaid ... ```), so pasting one into the editor would otherwise
 * produce an invalid definition. We strip the fences (and any prose around
 * the block) from the pasted text.
 */

// ```mermaid / ~~~mermaid / ``` (an info string may only follow the opening
// fence, and may not contain backticks)
const FENCE_LINE_REGEX = /^[ \t]*(`{3,}|~{3,})[ \t]*([^`\r\n]*?)[ \t]*$/;

type Fence = {
  lineIndex: number;
  marker: string;
  info: string;
};

type Block = {
  info: string;
  content: string;
};

const parseFenceLine = (line: string, lineIndex: number): Fence | null => {
  const match = line.match(FENCE_LINE_REGEX);

  if (!match) {
    return null;
  }

  return { lineIndex, marker: match[1][0], info: match[2] };
};

const hasContentAfter = (lines: string[], lineIndex: number) =>
  lines.slice(lineIndex + 1).some((line) => line.trim());

const getFencedBlocks = (lines: string[]): Block[] => {
  const blocks: Block[] = [];

  let openingFence: Fence | null = null;

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const fence = parseFenceLine(lines[lineIndex], lineIndex);

    if (!fence) {
      continue;
    }

    if (!openingFence) {
      // a bare fence with nothing after it closes a block that was opened
      // before the pasted text started
      if (!fence.info && !hasContentAfter(lines, lineIndex)) {
        blocks.push({
          info: "",
          content: lines.slice(0, lineIndex).join("\n"),
        });
        break;
      }
      openingFence = fence;
      continue;
    }

    // a closing fence must use the same marker and carry no info string
    if (fence.marker !== openingFence.marker || fence.info) {
      continue;
    }

    blocks.push({
      info: openingFence.info,
      content: lines.slice(openingFence.lineIndex + 1, lineIndex).join("\n"),
    });
    openingFence = null;
  }

  // unterminated block (e.g. pasting a partially streamed response)
  if (openingFence) {
    blocks.push({
      info: openingFence.info,
      content: lines.slice(openingFence.lineIndex + 1).join("\n"),
    });
  }

  return blocks;
};

const isMermaidInfoString = (info: string) =>
  info.trim().split(/\s+/)[0].toLowerCase() === "mermaid";

/**
 * Extracts the mermaid definition out of a markdown code block, if the text
 * contains one. Text without code fences is returned as is.
 */
export const stripMermaidCodeFences = (text: string): string => {
  if (!text.includes("```") && !text.includes("~~~")) {
    return text;
  }

  const blocks = getFencedBlocks(text.split(/\r?\n/));

  if (!blocks.length) {
    return text;
  }

  const block =
    blocks.find(({ info }) => isMermaidInfoString(info)) ??
    blocks.find(({ content }) => isMaybeMermaidDefinition(content)) ??
    blocks[0];

  return block.content.replace(/^[ \t]*\n+/, "").replace(/\s+$/, "");
};
